import type {
  ChessColor,
  ChessPieceType,
  ChessPiece,
  ChessMove,
  ChessGameState,
} from '../types/chess.ts';

/**
 * Creates standard 32 pieces of International Chess
 */
export function createInitialChessPieces(): ChessPiece[] {
  const pieces: ChessPiece[] = [];

  // Black back rank (Row 0)
  const blackBackOrder: ChessPieceType[] = ['rook', 'knight', 'bishop', 'queen', 'king', 'bishop', 'knight', 'rook'];
  for (let c = 0; c < 8; c++) {
    pieces.push({
      id: `b_${blackBackOrder[c]}_${c}`,
      type: blackBackOrder[c],
      color: 'black',
      row: 0,
      col: c,
      hasMoved: false,
    });
  }

  // Black pawns (Row 1)
  for (let c = 0; c < 8; c++) {
    pieces.push({
      id: `b_pawn_${c}`,
      type: 'pawn',
      color: 'black',
      row: 1,
      col: c,
      hasMoved: false,
    });
  }

  // White pawns (Row 6)
  for (let c = 0; c < 8; c++) {
    pieces.push({
      id: `w_pawn_${c}`,
      type: 'pawn',
      color: 'white',
      row: 6,
      col: c,
      hasMoved: false,
    });
  }

  // White back rank (Row 7)
  const whiteBackOrder: ChessPieceType[] = ['rook', 'knight', 'bishop', 'queen', 'king', 'bishop', 'knight', 'rook'];
  for (let c = 0; c < 8; c++) {
    pieces.push({
      id: `w_${whiteBackOrder[c]}_${c}`,
      type: whiteBackOrder[c],
      color: 'white',
      row: 7,
      col: c,
      hasMoved: false,
    });
  }

  return pieces;
}

export function createInitialChessGame(): ChessGameState {
  return {
    pieces: createInitialChessPieces(),
    turn: 'white',
    isCheck: false,
    isCheckmate: false,
    isStalemate: false,
    winner: null,
    moveHistory: [],
    lastMove: null,
    enPassantTarget: null,
  };
}

export function getChessPieceAt(pieces: ChessPiece[], row: number, col: number): ChessPiece | null {
  return pieces.find((p) => p.row === row && p.col === col) || null;
}

/**
 * Checks if a square is attacked by any piece of attackerColor
 */
export function isSquareAttacked(
  pieces: ChessPiece[],
  row: number,
  col: number,
  attackerColor: ChessColor
): boolean {
  const attackers = pieces.filter((p) => p.color === attackerColor);

  for (const p of attackers) {
    const dr = row - p.row;
    const dc = col - p.col;

    switch (p.type) {
      case 'pawn': {
        const attackRow = p.color === 'white' ? p.row - 1 : p.row + 1;
        if (row === attackRow && (col === p.col - 1 || col === p.col + 1)) {
          return true;
        }
        break;
      }

      case 'knight': {
        if ((Math.abs(dr) === 2 && Math.abs(dc) === 1) || (Math.abs(dr) === 1 && Math.abs(dc) === 2)) {
          return true;
        }
        break;
      }

      case 'bishop': {
        if (Math.abs(dr) === Math.abs(dc) && dr !== 0) {
          if (isRayClear(pieces, p.row, p.col, row, col)) return true;
        }
        break;
      }

      case 'rook': {
        if ((dr === 0 && dc !== 0) || (dc === 0 && dr !== 0)) {
          if (isRayClear(pieces, p.row, p.col, row, col)) return true;
        }
        break;
      }

      case 'queen': {
        if ((Math.abs(dr) === Math.abs(dc) && dr !== 0) || (dr === 0 && dc !== 0) || (dc === 0 && dr !== 0)) {
          if (isRayClear(pieces, p.row, p.col, row, col)) return true;
        }
        break;
      }

      case 'king': {
        if (Math.abs(dr) <= 1 && Math.abs(dc) <= 1 && (dr !== 0 || dc !== 0)) {
          return true;
        }
        break;
      }
    }
  }

  return false;
}

function isRayClear(pieces: ChessPiece[], fromR: number, fromC: number, toR: number, toC: number): boolean {
  const stepR = Math.sign(toR - fromR);
  const stepC = Math.sign(toC - fromC);
  let currR = fromR + stepR;
  let currC = fromC + stepC;

  while (currR !== toR || currC !== toC) {
    if (getChessPieceAt(pieces, currR, currC)) return false;
    currR += stepR;
    currC += stepC;
  }

  return true;
}

/**
 * Checks if the King of kingColor is in check
 */
export function isChessKingInCheck(pieces: ChessPiece[], kingColor: ChessColor): boolean {
  const king = pieces.find((p) => p.type === 'king' && p.color === kingColor);
  if (!king) return true;

  const oppColor: ChessColor = kingColor === 'white' ? 'black' : 'white';
  return isSquareAttacked(pieces, king.row, king.col, oppColor);
}

/**
 * Returns pseudo-legal moves for a piece (ignoring King check)
 */
export function getPseudoLegalMoves(
  piece: ChessPiece,
  pieces: ChessPiece[],
  enPassantTarget?: { row: number; col: number } | null
): { toRow: number; toCol: number; isCastling?: boolean; isEnPassant?: boolean }[] {
  const moves: { toRow: number; toCol: number; isCastling?: boolean; isEnPassant?: boolean }[] = [];
  const { row: r, col: c, color, type } = piece;

  const canOccupy = (tr: number, tc: number): boolean => {
    if (tr < 0 || tr > 7 || tc < 0 || tc > 7) return false;
    const target = getChessPieceAt(pieces, tr, tc);
    return !target || target.color !== color;
  };

  const isSquareEmpty = (tr: number, tc: number): boolean => {
    return tr >= 0 && tr <= 7 && tc >= 0 && tc <= 7 && !getChessPieceAt(pieces, tr, tc);
  };

  switch (type) {
    case 'pawn': {
      const isWhite = color === 'white';
      const forward = isWhite ? -1 : 1;
      const startRow = isWhite ? 6 : 1;

      // 1 step forward
      if (isSquareEmpty(r + forward, c)) {
        moves.push({ toRow: r + forward, toCol: c });

        // 2 steps forward from initial rank
        if (r === startRow && isSquareEmpty(r + 2 * forward, c)) {
          moves.push({ toRow: r + 2 * forward, toCol: c });
        }
      }

      // Diagonal captures
      for (const dc of [-1, 1]) {
        const tr = r + forward;
        const tc = c + dc;
        if (tr >= 0 && tr <= 7 && tc >= 0 && tc <= 7) {
          const target = getChessPieceAt(pieces, tr, tc);
          if (target && target.color !== color) {
            moves.push({ toRow: tr, toCol: tc });
          } else if (enPassantTarget && enPassantTarget.row === tr && enPassantTarget.col === tc) {
            moves.push({ toRow: tr, toCol: tc, isEnPassant: true });
          }
        }
      }
      break;
    }

    case 'knight': {
      const jumps = [
        [-2, -1], [-2, 1], [-1, -2], [-1, 2],
        [1, -2], [1, 2], [2, -1], [2, 1],
      ];
      for (const [dr, dc] of jumps) {
        const tr = r + dr;
        const tc = c + dc;
        if (canOccupy(tr, tc)) moves.push({ toRow: tr, toCol: tc });
      }
      break;
    }

    case 'bishop': {
      const dirs = [[-1, -1], [-1, 1], [1, -1], [1, 1]];
      for (const [dr, dc] of dirs) {
        let tr = r + dr;
        let tc = c + dc;
        while (tr >= 0 && tr <= 7 && tc >= 0 && tc <= 7) {
          const target = getChessPieceAt(pieces, tr, tc);
          if (!target) {
            moves.push({ toRow: tr, toCol: tc });
          } else {
            if (target.color !== color) moves.push({ toRow: tr, toCol: tc });
            break;
          }
          tr += dr;
          tc += dc;
        }
      }
      break;
    }

    case 'rook': {
      const dirs = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      for (const [dr, dc] of dirs) {
        let tr = r + dr;
        let tc = c + dc;
        while (tr >= 0 && tr <= 7 && tc >= 0 && tc <= 7) {
          const target = getChessPieceAt(pieces, tr, tc);
          if (!target) {
            moves.push({ toRow: tr, toCol: tc });
          } else {
            if (target.color !== color) moves.push({ toRow: tr, toCol: tc });
            break;
          }
          tr += dr;
          tc += dc;
        }
      }
      break;
    }

    case 'queen': {
      const dirs = [
        [-1, -1], [-1, 1], [1, -1], [1, 1],
        [-1, 0], [1, 0], [0, -1], [0, 1],
      ];
      for (const [dr, dc] of dirs) {
        let tr = r + dr;
        let tc = c + dc;
        while (tr >= 0 && tr <= 7 && tc >= 0 && tc <= 7) {
          const target = getChessPieceAt(pieces, tr, tc);
          if (!target) {
            moves.push({ toRow: tr, toCol: tc });
          } else {
            if (target.color !== color) moves.push({ toRow: tr, toCol: tc });
            break;
          }
          tr += dr;
          tc += dc;
        }
      }
      break;
    }

    case 'king': {
      const steps = [
        [-1, -1], [-1, 0], [-1, 1],
        [0, -1],           [0, 1],
        [1, -1],  [1, 0],  [1, 1],
      ];
      for (const [dr, dc] of steps) {
        const tr = r + dr;
        const tc = c + dc;
        if (canOccupy(tr, tc)) moves.push({ toRow: tr, toCol: tc });
      }

      // Castling
      if (!piece.hasMoved && !isChessKingInCheck(pieces, color)) {
        const oppColor: ChessColor = color === 'white' ? 'black' : 'white';
        // Kingside (Col 7)
        const kingsideRook = getChessPieceAt(pieces, r, 7);
        if (kingsideRook && kingsideRook.type === 'rook' && !kingsideRook.hasMoved) {
          if (isSquareEmpty(r, 5) && isSquareEmpty(r, 6)) {
            if (!isSquareAttacked(pieces, r, 5, oppColor) && !isSquareAttacked(pieces, r, 6, oppColor)) {
              moves.push({ toRow: r, toCol: 6, isCastling: true });
            }
          }
        }

        // Queenside (Col 0)
        const queensideRook = getChessPieceAt(pieces, r, 0);
        if (queensideRook && queensideRook.type === 'rook' && !queensideRook.hasMoved) {
          if (isSquareEmpty(r, 1) && isSquareEmpty(r, 2) && isSquareEmpty(r, 3)) {
            if (!isSquareAttacked(pieces, r, 2, oppColor) && !isSquareAttacked(pieces, r, 3, oppColor)) {
              moves.push({ toRow: r, toCol: 2, isCastling: true });
            }
          }
        }
      }
      break;
    }
  }

  return moves;
}

/**
 * Returns strictly legal moves (guarantees King does not end up in check)
 */
export function getChessLegalMovesForPiece(
  piece: ChessPiece,
  pieces: ChessPiece[],
  enPassantTarget?: { row: number; col: number } | null
): { toRow: number; toCol: number; isCastling?: boolean; isEnPassant?: boolean }[] {
  const pseudoMoves = getPseudoLegalMoves(piece, pieces, enPassantTarget);

  return pseudoMoves.filter((m) => {
    // Simulate move
    let nextPieces = pieces.filter((p) => !(p.row === m.toRow && p.col === m.toCol));

    if (m.isEnPassant) {
      // Remove captured pawn behind target
      const pawnRow = piece.color === 'white' ? m.toRow + 1 : m.toRow - 1;
      nextPieces = nextPieces.filter((p) => !(p.row === pawnRow && p.col === m.toCol));
    }

    nextPieces = nextPieces.map((p) => (p.id === piece.id ? { ...p, row: m.toRow, col: m.toCol } : p));

    // If castling, also move rook
    if (m.isCastling) {
      if (m.toCol === 6) {
        // Kingside: Rook from col 7 to col 5
        nextPieces = nextPieces.map((p) => (p.row === piece.row && p.col === 7 ? { ...p, col: 5 } : p));
      } else if (m.toCol === 2) {
        // Queenside: Rook from col 0 to col 3
        nextPieces = nextPieces.map((p) => (p.row === piece.row && p.col === 0 ? { ...p, col: 3 } : p));
      }
    }

    return !isChessKingInCheck(nextPieces, piece.color);
  });
}

/**
 * Returns all legal moves for a color
 */
export function getAllChessLegalMoves(
  color: ChessColor,
  pieces: ChessPiece[],
  enPassantTarget?: { row: number; col: number } | null
): { piece: ChessPiece; toRow: number; toCol: number; isCastling?: boolean; isEnPassant?: boolean }[] {
  const result: { piece: ChessPiece; toRow: number; toCol: number; isCastling?: boolean; isEnPassant?: boolean }[] = [];
  const myPieces = pieces.filter((p) => p.color === color);

  for (const p of myPieces) {
    const validDests = getChessLegalMovesForPiece(p, pieces, enPassantTarget);
    for (const d of validDests) {
      result.push({ piece: p, ...d });
    }
  }

  return result;
}

/**
 * Creates algebraic notation for a move
 */
export function formatChessNotation(
  piece: ChessPiece,
  toRow: number,
  toCol: number,
  isCapture: boolean,
  isCheck: boolean,
  isCheckmate: boolean,
  isCastling?: boolean
): string {
  if (isCastling) {
    return toCol === 6 ? 'O-O' : 'O-O-O';
  }

  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
  const rank = 8 - toRow;
  const square = `${files[toCol]}${rank}`;

  let prefix = '';
  switch (piece.type) {
    case 'king': prefix = 'K'; break;
    case 'queen': prefix = 'Q'; break;
    case 'rook': prefix = 'R'; break;
    case 'bishop': prefix = 'B'; break;
    case 'knight': prefix = 'N'; break;
    case 'pawn':
      prefix = isCapture ? files[piece.col] : '';
      break;
  }

  const capStr = isCapture ? 'x' : '';
  const suffix = isCheckmate ? '#' : isCheck ? '+' : '';

  return `${prefix}${capStr}${square}${suffix}`;
}

/**
 * Applies a chess move and updates the game state
 */
export function applyChessMove(
  state: ChessGameState,
  fromRow: number,
  fromCol: number,
  toRow: number,
  toCol: number,
  promoteTo: ChessPieceType = 'queen'
): ChessGameState {
  const piece = getChessPieceAt(state.pieces, fromRow, fromCol);
  if (!piece || piece.color !== state.turn) return state;

  const legalMoves = getChessLegalMovesForPiece(piece, state.pieces, state.enPassantTarget);
  const moveMeta = legalMoves.find((m) => m.toRow === toRow && m.toCol === toCol);
  if (!moveMeta) return state;

  const capturedPiece = getChessPieceAt(state.pieces, toRow, toCol);
  const isEnPassant = moveMeta.isEnPassant;
  const isCastling = moveMeta.isCastling;

  // New pieces array
  let nextPieces = state.pieces.filter((p) => !(p.row === toRow && p.col === toCol));

  if (isEnPassant) {
    const pawnRow = piece.color === 'white' ? toRow + 1 : toRow - 1;
    nextPieces = nextPieces.filter((p) => !(p.row === pawnRow && p.col === toCol));
  }

  // Handle Pawn Promotion
  const isPromotion = piece.type === 'pawn' && (toRow === 0 || toRow === 7);
  const finalType = isPromotion ? promoteTo : piece.type;

  nextPieces = nextPieces.map((p) =>
    p.id === piece.id
      ? { ...p, row: toRow, col: toCol, type: finalType, hasMoved: true }
      : p
  );

  // Handle Castling Rook Movement
  if (isCastling) {
    if (toCol === 6) {
      nextPieces = nextPieces.map((p) =>
        p.row === piece.row && p.col === 7 ? { ...p, col: 5, hasMoved: true } : p
      );
    } else if (toCol === 2) {
      nextPieces = nextPieces.map((p) =>
        p.row === piece.row && p.col === 0 ? { ...p, col: 3, hasMoved: true } : p
      );
    }
  }

  // Set En Passant Target for 2-step pawn advances
  let nextEnPassant: { row: number; col: number } | null = null;
  if (piece.type === 'pawn' && Math.abs(toRow - fromRow) === 2) {
    nextEnPassant = { row: (fromRow + toRow) / 2, col: fromCol };
  }

  const nextTurn: ChessColor = state.turn === 'white' ? 'black' : 'white';
  const isCheck = isChessKingInCheck(nextPieces, nextTurn);
  const nextLegalMoves = getAllChessLegalMoves(nextTurn, nextPieces, nextEnPassant);

  let isCheckmate = false;
  let isStalemate = false;
  let winner = state.winner;

  if (nextLegalMoves.length === 0) {
    if (isCheck) {
      isCheckmate = true;
      winner = state.turn; // Attacker won!
    } else {
      isStalemate = true;
      winner = 'draw';
    }
  }

  const notation = formatChessNotation(
    piece,
    toRow,
    toCol,
    !!capturedPiece || !!isEnPassant,
    isCheck,
    isCheckmate,
    isCastling
  );

  const moveRecord: ChessMove = {
    fromRow,
    fromCol,
    toRow,
    toCol,
    piece: { ...piece },
    captured: capturedPiece || undefined,
    promotedTo: isPromotion ? promoteTo : undefined,
    isCastling,
    isEnPassant,
    notation,
  };

  return {
    pieces: nextPieces,
    turn: nextTurn,
    isCheck,
    isCheckmate,
    isStalemate,
    winner,
    moveHistory: [moveRecord, ...state.moveHistory],
    lastMove: moveRecord,
    enPassantTarget: nextEnPassant,
  };
}

/**
 * Material weights for Chess evaluation
 */
const CHESS_PIECE_VALUES: Record<ChessPieceType, number> = {
  pawn: 100,
  knight: 320,
  bishop: 330,
  rook: 500,
  queen: 900,
  king: 20000,
};

export function evaluateChessBoard(pieces: ChessPiece[]): number {
  let score = 0;
  for (const p of pieces) {
    let val = CHESS_PIECE_VALUES[p.type];

    // Positional center bonuses
    if (p.row >= 3 && p.row <= 4 && p.col >= 3 && p.col <= 4) {
      val += 25;
    } else if (p.row >= 2 && p.row <= 5 && p.col >= 2 && p.col <= 5) {
      val += 10;
    }

    if (p.color === 'white') {
      score += val;
    } else {
      score -= val;
    }
  }
  return score;
}

/**
 * Minimax AI for Chess with Alpha-Beta pruning
 */
export function getBestChessAIMove(
  state: ChessGameState,
  depth: number = 2
): { fromRow: number; fromCol: number; toRow: number; toCol: number } | null {
  const legalMoves = getAllChessLegalMoves(state.turn, state.pieces, state.enPassantTarget);
  if (legalMoves.length === 0) return null;

  const isMaximizing = state.turn === 'white';
  let bestScore = isMaximizing ? -Infinity : Infinity;
  let bestMove: { fromRow: number; fromCol: number; toRow: number; toCol: number } | null = null;

  const shuffled = [...legalMoves].sort(() => Math.random() - 0.5);

  for (const m of shuffled) {
    const nextState = applyChessMove(state, m.piece.row, m.piece.col, m.toRow, m.toCol);
    const score = minimaxChess(nextState, depth - 1, -Infinity, Infinity, !isMaximizing);

    if (isMaximizing) {
      if (score > bestScore) {
        bestScore = score;
        bestMove = { fromRow: m.piece.row, fromCol: m.piece.col, toRow: m.toRow, toCol: m.toCol };
      }
    } else {
      if (score < bestScore) {
        bestScore = score;
        bestMove = { fromRow: m.piece.row, fromCol: m.piece.col, toRow: m.toRow, toCol: m.toCol };
      }
    }
  }

  return bestMove || {
    fromRow: shuffled[0].piece.row,
    fromCol: shuffled[0].piece.col,
    toRow: shuffled[0].toRow,
    toCol: shuffled[0].toCol,
  };
}

function minimaxChess(
  state: ChessGameState,
  depth: number,
  alpha: number,
  beta: number,
  isMaximizing: boolean
): number {
  if (depth === 0 || state.winner !== null) {
    return evaluateChessBoard(state.pieces);
  }

  const legalMoves = getAllChessLegalMoves(state.turn, state.pieces, state.enPassantTarget);
  if (legalMoves.length === 0) {
    return evaluateChessBoard(state.pieces);
  }

  if (isMaximizing) {
    let maxEval = -Infinity;
    for (const m of legalMoves) {
      const nextState = applyChessMove(state, m.piece.row, m.piece.col, m.toRow, m.toCol);
      const evalScore = minimaxChess(nextState, depth - 1, alpha, beta, false);
      maxEval = Math.max(maxEval, evalScore);
      alpha = Math.max(alpha, evalScore);
      if (beta <= alpha) break;
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (const m of legalMoves) {
      const nextState = applyChessMove(state, m.piece.row, m.piece.col, m.toRow, m.toCol);
      const evalScore = minimaxChess(nextState, depth - 1, alpha, beta, true);
      minEval = Math.min(minEval, evalScore);
      beta = Math.min(beta, evalScore);
      if (beta <= alpha) break;
    }
    return minEval;
  }
}
